import { useRef, useState, type ReactNode } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Download } from 'lucide-react'
import { downloadTree } from '@agribot/sourcing-model'

/**
 * A diagram full size, with saving it as an image: the composition tree
 * (RFI Figures 2–3) or the group structure (Figure 4). `children` renders
 * the SVG and receives the ref to save.
 */
export function DiagramDialog({ title, fileName, onClose, children }: {
  title: string
  fileName: string
  onClose: () => void
  children: (ref: React.RefObject<SVGSVGElement>) => ReactNode
}) {
  const ref = useRef<SVGSVGElement>(null)
  const [error, setError] = useState<string | null>(null)
  const save = (format: 'svg' | 'png') => {
    if (!ref.current) return
    downloadTree(ref.current, fileName, format).catch((e: Error) => setError(e.message))
  }
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { maxWidth: 'calc(100vw - 64px)' } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', maxHeight: '70vh' }}>{children(ref)}</Box>
        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
        <Button variant="outlined" startIcon={<Download size={15} />} onClick={() => save('png')}>Save as PNG</Button>
        <Button variant="outlined" startIcon={<Download size={15} />} onClick={() => save('svg')}>Save as SVG</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
